import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExportFilenameService } from './export-filename.service';

@Component({
  selector: 'app-export-filename-dialog',
  templateUrl: './export-filename-dialog.component.html',
  styleUrls: ['./export-filename-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportFilenameDialogComponent implements OnInit, OnDestroy {
  @ViewChild('filenameInput') filenameInput: ElementRef<HTMLInputElement>;

  public visible = false;
  public filename = '';

  private activeResult: Subject<string>;
  private originalExtension = '';
  private resolved = false;
  private destroy$ = new Subject<void>();

  constructor(
    private exportFilenameService: ExportFilenameService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.exportFilenameService.prompts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ defaultFilename, result }) => {
        this.activeResult = result;
        this.originalExtension = this.extractExtension(defaultFilename);
        this.filename = defaultFilename;
        this.resolved = false;
        this.visible = true;
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isSaveDisabled(): boolean {
    return !this.stripExtension(this.filename).trim();
  }

  handleShow(): void {
    const input = this.filenameInput?.nativeElement;
    if (!input) {
      return;
    }
    input.focus();
    const baseNameLength = this.stripExtension(this.filename).length;
    input.setSelectionRange(0, baseNameLength);
  }

  handleSave(): void {
    if (this.isSaveDisabled) {
      return;
    }
    const sanitized = this.exportFilenameService.sanitizeFilename(this.filename);
    this.resolveWith(this.applyExtension(sanitized));
  }

  handleCancel(): void {
    this.resolveWith(null);
  }

  handleHide(): void {
    if (!this.resolved) {
      this.resolveWith(null);
    }
  }

  private resolveWith(filename: string): void {
    this.resolved = true;
    this.visible = false;
    this.activeResult?.next(filename);
    this.activeResult?.complete();
    this.activeResult = null;
  }

  private extractExtension(filename: string): string {
    const match = /\.([^.\\/]+)$/.exec(filename || '');
    return match ? match[1] : '';
  }

  private stripExtension(filename: string): string {
    return (filename || '').replace(/\.[^.\\/]+$/, '');
  }

  private applyExtension(filename: string): string {
    if (!this.originalExtension) {
      return filename;
    }
    const currentExtension = this.extractExtension(filename);
    if (currentExtension.toLowerCase() === this.originalExtension.toLowerCase()) {
      return filename;
    }
    return `${this.stripExtension(filename)}.${this.originalExtension}`;
  }
}
